import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureSqlDatabaseLinkedService } from 'app/model/linkedservice/azure-sql.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import {
    akvToResource,
    akvToModel,
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { ValueAdapter } from '../value.adapter';
import {
    connectionStringPartToResource,
    connectionStringPartToModel
} from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { ReferenceAdapter } from '../reference.adapter';
import {
    Credential,
    AdapterContext,
    CredentialTypeOptions,
    azureSqlAuthRefTypes,
    azureCloudTypeDef,
    ITridentResource,
    ConnectionAuthKind,
    ParameterConfig,
    sqlAdditionalPropertiesKeyList,
    SqlAdditionalPropertiesKeyType,
    sqlAdditionalPropertiesAdapts,
    AuthenticationType,
    SQLAuthenticationType
} from 'shell-models-lib';
import {
    getEncryptionAuthTypeToModel,
    getEncryptionAuthTypeToResource,
    sqlAdditionalPropertiesKeyOptions
} from 'app/registries/consts/metadata-consts';
import { compact, isEmpty } from 'lodash';
import { GlobalVariables } from 'app/services/globals';
import { toParameterClass, toParameterObject } from './parameter-config.util';

const sqlDatabaseV1Adapts = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    connectionString: (_context: AdapterContext, _value: any, ls: AzureSqlDatabaseLinkedService) => {
        // when AKV is used for connection string, it is already well handled. So we don't need handle it.
        // won't cover fully by json for now
        if (ls && !ls.fullyByJsonEditor && !ls.enableAKV) {
            if (ls.authenticationType === SQLAuthenticationType.SQL && ls.password instanceof AzureKeyVault) {
                return '*';
            }
            if (
                ls.authenticationType === SQLAuthenticationType.ManagedServiceIdentity ||
                ls.authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity ||
                ls.authenticationType === SQLAuthenticationType.UserAuth ||
                ls.authenticationType === SQLAuthenticationType.Credential
            ) {
                return '*';
            }
            if (
                ls.authenticationType === SQLAuthenticationType.ServicePrincipal &&
                (ls.servicePrincipalKey instanceof AzureKeyVault ||
                    (ls.credential != null && ls.credential.getType() === CredentialTypeOptions.ServicePrincipal))
            ) {
                return '*';
            }
        }
        return 'secret';
    },
    servicePrincipalKey: 'secret'
};

const sqlDatabaseV2Adapts = {
    server: '*',
    database: '*',
    userName: '*',
    encrypt: '*',
    trustServerCertificate: '*',
    hostNameInCertificate: '*',
    authenticationType: '*',
    servicePrincipalCredential: 'secret',
    ...sqlAdditionalPropertiesAdapts
};

const sharedAdapts = {
    password: 'secret',
    encryptedCredential: '*',
    servicePrincipalId: '*',
    tenant: '*',
    azureCloudType: '*',
    credential: '*',
    alwaysEncryptedSettings: {
        alwaysEncryptedAkvAuthType: '*',
        servicePrincipalId: '*',
        servicePrincipalKey: 'secret',
        credential: '*'
    }
};

export const sqlConnectionStringKeyAlias = {
    'Data Source': ['Data Source', 'Server', 'Address', 'Addr', 'Network Address'],
    'Initial Catalog': ['Initial Catalog', 'Database'],
    'User ID': ['User ID', 'UID', 'User'],
    'Password': ['Password', 'PWD'],
    'Trust Server Certificate': ['Trust Server Certificate', 'TrustServerCertificate'],
    'Host Name In Certificate': ['Host Name In Certificate', 'HostNameInCertificate'],
    'Integrated Security': ['Integrated Security', 'Trusted_Connection'],
    'Connection Timeout': ['Connection Timeout', 'Connect Timeout', 'Timeout']
};

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureSqlDatabaseLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                ...sharedAdapts,
                ...sqlDatabaseV1Adapts, // if v1 is never supported, we should remove this
                ...sqlDatabaseV2Adapts
            },
            parameters: '*'
        }
    }
})
export class AzureSqlLinkedServiceAdapter extends LinkedServiceAdapter<AzureSqlDatabaseLinkedService> {
    protected supportFirstClassParameterExperience = true;
    /**
     * Update AuthenticationType based on fetched credential type
     * @param model linked service model
     */
    public async postToModel(model: AzureSqlDatabaseLinkedService): Promise<void> {
        if (model.sqlVersion === 'v2') {
            return;
        }
        let finalAuthType: SQLAuthenticationType = model.authenticationType;
        if (model.credential) {
            const credType = model.credential.getType();

            if (credType === CredentialTypeOptions.ServicePrincipal) {
                finalAuthType = SQLAuthenticationType.ServicePrincipal;
            } else if (credType === CredentialTypeOptions.UserAuth) {
                finalAuthType = SQLAuthenticationType.UserAuth;
            } else if (credType === CredentialTypeOptions.ManagedIdentity) {
                finalAuthType = SQLAuthenticationType.ManagedServiceIdentity;
            } else if (credType === CredentialTypeOptions.UserAssignedManagedIdentity) {
                finalAuthType = SQLAuthenticationType.UserAssignedManagedIdentity;
            } else {
                finalAuthType = model.authenticationType;
            }
        }
        if (model.isExternalConnection && model.extension._credentialType$ === ConnectionAuthKind.OAuth2) {
            // Trident OAuth2
            finalAuthType = SQLAuthenticationType.UserAuth;
            model.authReferenceMethod = undefined;
        }
        model.authenticationType = finalAuthType;
    }

    protected modelConstructor(): new () => AzureSqlDatabaseLinkedService {
        return AzureSqlDatabaseLinkedService;
    }

    protected model(): IModel {
        return AzureSqlDatabaseLinkedService.MODEL;
    }

    protected override toAzureTypeProperties(tridentResource: ITridentResource) {
        const result: Partial<Model.AzureSqlDatabaseLinkedServiceTypeProperties> = {};
        const builder = new DbConnectionStringBuilderService();
        const DUMMY_VALUE = '*'; // use it when you just want an non-empty value

        switch (tridentResource.credentialDetails.credentialType) {
            case ConnectionAuthKind.Basic:
                builder.add('User ID', DUMMY_VALUE);
                break;
            case ConnectionAuthKind.ServicePrincipal:
                result.servicePrincipalId = DUMMY_VALUE;
                break;
            default:
                break;
        }

        try {
            const { database, server } = JSON.parse(tridentResource.connectionDetails);
            if (!database && !server) {
                return undefined;
            }
            builder.add('Data Source', server);
            builder.add('Initial Catalog', database);
            result.connectionString = builder.connectionString;
        } catch (error) {
            // noop
        }

        return isEmpty(result) ? undefined : result;
    }

    protected override _parseConnectionString(
        connectionString: string,
        linkedService: AzureSqlDatabaseLinkedService,
        additionalFieldsParameterizable: boolean = false
    ): void {
        try {
            this._updateModelFromConnectionString(
                connectionString,
                linkedService,
                false,
                additionalFieldsParameterizable
            );
            const hasUserId =
                !stringIsNullOrEmpty(AzureSqlDatabaseLinkedService.USER_NAME.templateValue(linkedService)) ||
                !stringIsNullOrEmpty(AzureSqlDatabaseLinkedService.USER_NAME.value(linkedService));
            if (hasUserId) {
                linkedService.authenticationType = SQLAuthenticationType.SQL;
            }
        } catch (e) {
            this._logError(
                `AzureSqlLinkedServiceAdapter parse connection string error: "connection string": ${connectionString}, "error":${e}`
            );
        }
    }

    protected async propertiesToResourceAsync(
        linkedService: AzureSqlDatabaseLinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureSqlDatabaseLinkedService;
        properties.type = this.model().name;

        let servicePrincipalId: string;
        let servicePrincipalKey: string | Model.SecretBase;
        let tenant: string;
        let azureCloudType: azureCloudTypeDef;

        const isSP = linkedService.authenticationType === SQLAuthenticationType.ServicePrincipal;
        const isInlineSP =
            isSP && linkedService.authReferenceMethod === azureSqlAuthRefTypes.InlineRefTypeAuthentication;
        if (isInlineSP) {
            servicePrincipalId = this.valueToResource(
                linkedService,
                AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_ID
            );
            servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_KEY,
                this.context
            );
            tenant = this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.TENANT);
            azureCloudType = linkedService.azureCloudType;
        }

        const credential =
            linkedService.credential &&
            ((isSP && !isInlineSP) ||
                linkedService.authenticationType === SQLAuthenticationType.UserAssignedManagedIdentity ||
                (GlobalVariables.isSamiSupportEnabled &&
                    linkedService.authenticationType === SQLAuthenticationType.ManagedServiceIdentity &&
                    linkedService.authReferenceMethod === azureSqlAuthRefTypes.CredentialRefTypeAuthentication) ||
                linkedService.authenticationType === SQLAuthenticationType.UserAuth)
                ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                : undefined;

        if (linkedService.sqlVersion === 'v2') {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.SERVER_NAME),
                database: this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.DATABASE_NAME),
                encrypt: this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.ENCRYPT),
                trustServerCertificate: this.valueToResource(
                    linkedService,
                    AzureSqlDatabaseLinkedService.TRUST_SERVER_CERTIFICATE
                ),
                hostNameInCertificate:
                    (AzureSqlDatabaseLinkedService.HOST_NAME_IN_CERTIFICATE.templateValue(linkedService) ||
                        linkedService.hostNameInCertificate) &&
                    (!stringIsNullOrEmpty(
                        AzureSqlDatabaseLinkedService.TRUST_SERVER_CERTIFICATE.templateValue(linkedService)
                    ) ||
                        linkedService.trustServerCertificate !== true)
                        ? this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.HOST_NAME_IN_CERTIFICATE)
                        : undefined,
                authenticationType: linkedService.authenticationType,
                servicePrincipalId,
                servicePrincipalCredential: isInlineSP ? servicePrincipalKey : undefined,
                tenant,
                azureCloudType,
                userName:
                    linkedService.authenticationType === SQLAuthenticationType.SQL
                        ? this.valueToResource(linkedService, AzureSqlDatabaseLinkedService.USER_NAME)
                        : undefined,
                password:
                    linkedService.authenticationType === SQLAuthenticationType.SQL
                        ? linkedService.password instanceof AzureKeyVault
                            ? akvToResource(linkedService.password, this.context)
                            : parameterizablePasswordOrAKVtoSecretBase(
                                  linkedService,
                                  AzureSqlDatabaseLinkedService.PASSWORD,
                                  this.context
                              )
                        : undefined,
                credential
            };

            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }
        } else {
            let connectionString: string | Model.SecretBase;

            if (!linkedService.enableAKV) {
                const builder = new DbConnectionStringBuilderService();
                builder.add('Integrated Security', AzureSqlDatabaseLinkedService.integratedSecurity);
                builder.add('Encrypt', AzureSqlDatabaseLinkedService.encrypt);
                builder.add('Connection Timeout', AzureSqlDatabaseLinkedService.connectionTimeout);

                builder.add(
                    'Data Source',
                    connectionStringPartToResource(linkedService, AzureSqlDatabaseLinkedService.SERVER_NAME)
                );
                builder.add(
                    'Initial Catalog',
                    connectionStringPartToResource(linkedService, AzureSqlDatabaseLinkedService.DATABASE_NAME)
                );
                if (linkedService.authenticationType === SQLAuthenticationType.SQL) {
                    builder.add(
                        'User ID',
                        connectionStringPartToResource(linkedService, AzureSqlDatabaseLinkedService.USER_NAME)
                    );
                    if (!(linkedService.password instanceof AzureKeyVault)) {
                        const password = connectionStringPartToResource(
                            linkedService,
                            AzureSqlDatabaseLinkedService.PASSWORD
                        );
                        if (password) {
                            builder.add('Password', password);
                        }
                    }
                }
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        builder.add(pair.key, pair.value || '');
                    });
                }

                connectionString =
                    linkedService.serverName === Constants.maskedCredential &&
                    linkedService.databaseName === Constants.maskedCredential &&
                    linkedService.userName === Constants.maskedCredential &&
                    linkedService.password === Constants.maskedCredential
                        ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                          ({
                              type: 'SecureString',
                              value: Constants.maskedCredential
                          } as Model.SecureString)
                        : this._connectionStringToResource(builder.connectionString);
            } else {
                connectionString = akvToResource(linkedService.referedAKV, this.context);
            }

            properties.typeProperties = {
                connectionString,
                password:
                    !linkedService.enableAKV &&
                    linkedService.authenticationType === SQLAuthenticationType.SQL &&
                    linkedService.password instanceof AzureKeyVault
                        ? akvToResource(linkedService.password, this.context)
                        : undefined,
                servicePrincipalId,
                servicePrincipalKey,
                tenant,
                azureCloudType,
                credential
            };
        }
        if (!linkedService.alwaysEncrypted) {
            return linkedServiceResource;
        }

        const alwaysEncryptedSettings: Model.SqlEncryptionProperties = {
            alwaysEncryptedAkvAuthType: getEncryptionAuthTypeToResource(
                linkedService.keyStoreAuthType as AuthenticationType
            )
        };
        if (linkedService.keyStoreAuthType === AuthenticationType.ServicePrincipal) {
            alwaysEncryptedSettings.servicePrincipalId = this.valueToResource(
                linkedService,
                AzureSqlDatabaseLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_ID
            );
            alwaysEncryptedSettings.servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                AzureSqlDatabaseLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_KEY,
                this.context
            );
        } else if (
            linkedService.keyStoreAuthType === AuthenticationType.UserAssignedManagedIdentity ||
            linkedService.keyStoreAuthType === AuthenticationType.UserAuth
        ) {
            if (linkedService.alwaysEncryptionCredential != null) {
                alwaysEncryptedSettings.credential = ReferenceAdapter.toResource(
                    linkedService.alwaysEncryptionCredential,
                    this.context
                ) as Model.CredentialReference;
            }
        }
        properties.typeProperties.alwaysEncryptedSettings = alwaysEncryptedSettings;
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: AzureSqlDatabaseLinkedService
    ): Promise<AzureSqlDatabaseLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureSqlDatabaseLinkedService;

        if (!__IS_FABRIC_DPE__ && properties.typeProperties.connectionString == null) {
            linkedService.sqlVersion = 'v2';

            ValueAdapter.toModel(
                linkedService,
                AzureSqlDatabaseLinkedService.SERVER_NAME,
                properties.typeProperties.server
            );
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDatabaseLinkedService.DATABASE_NAME,
                properties.typeProperties.database
            );
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDatabaseLinkedService.ENCRYPT,
                properties.typeProperties.encrypt
            );
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDatabaseLinkedService.TRUST_SERVER_CERTIFICATE,
                properties.typeProperties.trustServerCertificate
            );
            ValueAdapter.toModel(
                linkedService,
                AzureSqlDatabaseLinkedService.HOST_NAME_IN_CERTIFICATE,
                properties.typeProperties.hostNameInCertificate
            );
            linkedService.authenticationType = properties.typeProperties.authenticationType as SQLAuthenticationType;

            if (linkedService.authenticationType === SQLAuthenticationType.SQL) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.USER_NAME,
                    properties.typeProperties.userName
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.PASSWORD,
                    properties.typeProperties.password,
                    this.context
                );
            } else if (
                linkedService.authenticationType === SQLAuthenticationType.ServicePrincipal &&
                properties.typeProperties.servicePrincipalId
            ) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.servicePrincipalCredential,
                    this.context
                );
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.TENANT,
                    properties.typeProperties.tenant
                );
                linkedService.authReferenceMethod = azureSqlAuthRefTypes.InlineRefTypeAuthentication;
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
                linkedService.authReferenceMethod = azureSqlAuthRefTypes.CredentialRefTypeAuthentication;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const additionalPropertiesObj: { [key: string]: any } = {};
            for (const propertyName in properties.typeProperties) {
                if (sqlAdditionalPropertiesKeyList.includes(propertyName)) {
                    additionalPropertiesObj[propertyName] =
                        properties.typeProperties[propertyName as keyof Model.SqlServerLinkedServiceTypeProperties];
                }
            }
            linkedService.parameterizableAdditionalFields = toParameterClass(
                additionalPropertiesObj,
                linkedService
            ) as Array<ParameterConfig<SqlAdditionalPropertiesKeyType>>;
        } else {
            linkedService.sqlVersion = 'v1';
            // Cleanup, in case non of the if-else statement got executed
            AzureSqlDatabaseLinkedService.SERVER_NAME.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.USER_NAME.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.PASSWORD.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_ID.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_ID.setTemplateValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_KEY.setValue(linkedService, undefined);
            AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_KEY.setTemplateValue(linkedService, undefined);
            linkedService.authenticationType = SQLAuthenticationType.SQL;
            linkedService.authReferenceMethod = properties.typeProperties.credential
                ? azureSqlAuthRefTypes.CredentialRefTypeAuthentication
                : azureSqlAuthRefTypes.InlineRefTypeAuthentication;
            linkedService.referedAKV = undefined;
            linkedService.credential = undefined;
            // secureStringGroups will be introduced after we add the component which let user switch between PlainText and SecureString
            // linkedService.secureStringGroups[secureStringGroupAzureSqlDatabaseLinkedServiceConnectionString] = 'PlainText';

            let hasUserId = false;
            if (isString(properties.typeProperties.connectionString)) {
                this._updateModelFromConnectionString(properties.typeProperties.connectionString, linkedService);
                hasUserId =
                    !stringIsNullOrEmpty(AzureSqlDatabaseLinkedService.USER_NAME.templateValue(linkedService)) ||
                    !stringIsNullOrEmpty(AzureSqlDatabaseLinkedService.USER_NAME.value(linkedService));
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        AzureSqlDatabaseLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.serverName = Constants.maskedCredential;
                linkedService.databaseName = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                if (properties.typeProperties.password) {
                    parameterizableSecretBaseToModel(
                        linkedService,
                        AzureSqlDatabaseLinkedService.PASSWORD,
                        properties.typeProperties.password,
                        this.context
                    );
                }
            } else if (
                (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret'
            ) {
                linkedService.referedAKV = akvToModel(
                    properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference,
                    this.context
                );
                linkedService.enableAKV = true;
                linkedService.authenticationType = SQLAuthenticationType.SqlOrManagedServiceIdentity;
            }

            // Since the authenticationType is not in the resource, no idea when to call linkedService.authenticationType = AuthenticationType.ManagedServiceIdentity
            if (properties.typeProperties.servicePrincipalId) {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.servicePrincipalKey,
                    this.context
                );
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.TENANT,
                    properties.typeProperties.tenant
                );
                linkedService.authenticationType = SQLAuthenticationType.ServicePrincipal;
                linkedService.authReferenceMethod = azureSqlAuthRefTypes.InlineRefTypeAuthentication;
                AzureSqlDatabaseLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
                linkedService.azureCloudType = properties.typeProperties.azureCloudType;
            } else if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential,
                    this.context
                );
                linkedService.authReferenceMethod = azureSqlAuthRefTypes.CredentialRefTypeAuthentication;
                AzureSqlDatabaseLinkedService.USER_NAME.setValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            } else if (isString(properties.typeProperties.connectionString) && !hasUserId) {
                linkedService.authenticationType = SQLAuthenticationType.ManagedServiceIdentity;
                AzureSqlDatabaseLinkedService.USER_NAME.setValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.PASSWORD.setValue(linkedService, undefined);
                AzureSqlDatabaseLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);
            }
        }

        if (properties.typeProperties.alwaysEncryptedSettings) {
            linkedService.alwaysEncrypted = true;
            const authType = properties.typeProperties.alwaysEncryptedSettings.alwaysEncryptedAkvAuthType;
            linkedService.keyStoreAuthType = getEncryptionAuthTypeToModel(authType);
            if (authType === 'ServicePrincipal') {
                ValueAdapter.toModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_ID,
                    properties.typeProperties.alwaysEncryptedSettings.servicePrincipalId
                );
                parameterizableSecretBaseToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.ENCRYPTION_SERVICE_PRINCIPAL_KEY,
                    properties.typeProperties.alwaysEncryptedSettings.servicePrincipalKey,
                    this.context
                );
            } else if (authType === 'UserAssignedManagedIdentity' || authType === AuthenticationType.UserAuth) {
                linkedService.alwaysEncryptionCredential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.alwaysEncryptedSettings.credential,
                    this.context
                );
            } else if (authType !== 'ManagedIdentity') {
                // unknown type
                this._logError(
                    `AzureSqlLinkedServiceAdapter encountered unknown alwaysEncryptedAkvAuthType: ${authType}`
                );
            }
        }

        return linkedService;
    }

    private _updateModelFromConnectionString(
        connectionString: string,
        linkedService: AzureSqlDatabaseLinkedService,
        fromLSResource: boolean = true,
        additionalFieldsParameterizable: boolean = false
    ): void {
        const builder = new DbConnectionStringBuilderService();
        builder.parse(connectionString);

        connectionStringPartToModel(
            linkedService,
            AzureSqlDatabaseLinkedService.SERVER_NAME,
            builder.getValueUsingAlias('Data Source', sqlConnectionStringKeyAlias, false) || ''
        );
        connectionStringPartToModel(
            linkedService,
            AzureSqlDatabaseLinkedService.DATABASE_NAME,
            builder.getValueUsingAlias('Initial Catalog', sqlConnectionStringKeyAlias, false) || (__IS_FABRIC_DPE__ ? undefined : '')
        );
        connectionStringPartToModel(
            linkedService,
            AzureSqlDatabaseLinkedService.USER_NAME,
            builder.getValueUsingAlias('User ID', sqlConnectionStringKeyAlias, false)
        );

        if (fromLSResource || builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false)) {
            connectionStringPartToModel(
                linkedService,
                AzureSqlDatabaseLinkedService.PASSWORD,
                builder.getValueUsingAlias('Password', sqlConnectionStringKeyAlias, false) || '',
                fromLSResource
            );
        }

        const remainedKeys = [
            ...sqlConnectionStringKeyAlias['Data Source'],
            ...sqlConnectionStringKeyAlias['Initial Catalog'],
            ...sqlConnectionStringKeyAlias['User ID'],
            ...sqlConnectionStringKeyAlias['Password']
        ];

        if (additionalFieldsParameterizable) {
            if (builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.ENCRYPT,
                    builder.getValueUsingAlias('Encrypt', sqlConnectionStringKeyAlias, false)
                );
            }

            const tscVaule = builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false);
            if (tscVaule) {
                const tscBooleanValue =
                    tscVaule.toLowerCase() === 'true' ? true : tscVaule.toLowerCase() === 'false' ? false : tscVaule;
                if (tscBooleanValue === true || tscBooleanValue === false) {
                    AzureSqlDatabaseLinkedService.TRUST_SERVER_CERTIFICATE.setValue(linkedService, tscBooleanValue);
                } else {
                    connectionStringPartToModel(
                        linkedService,
                        AzureSqlDatabaseLinkedService.TRUST_SERVER_CERTIFICATE,
                        builder.getValueUsingAlias('Trust Server Certificate', sqlConnectionStringKeyAlias, false)
                    );
                }
            }

            if (builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)) {
                connectionStringPartToModel(
                    linkedService,
                    AzureSqlDatabaseLinkedService.HOST_NAME_IN_CERTIFICATE,
                    builder.getValueUsingAlias('Host Name In Certificate', sqlConnectionStringKeyAlias, false)
                );
            }

            remainedKeys.push(...[
                'Encrypt',
                ...sqlConnectionStringKeyAlias['Trust Server Certificate'] || 'Trust Server Certificate'
            ]);

            linkedService.parameterizableAdditionalFields = compact(
                builder.getRemainedKeyValuePairs(remainedKeys).map((kv) => {
                    const parameterObject = new ParameterConfig<SqlAdditionalPropertiesKeyType>();
                    const additionalParameter = sqlAdditionalPropertiesKeyOptions.find(
                        (option) => option.value.toLowerCase() === kv.key.toLowerCase().replace(/\s*/g, '')
                    );
                    if (additionalParameter) {
                        parameterObject.name = additionalParameter.value as SqlAdditionalPropertiesKeyType;
                        parameterObject.value = kv.value;
                        parameterObject.type = additionalParameter.type;
                        return parameterObject;
                    }
                    return undefined;
                })
            );
        } else {
            linkedService.additionalFields = builder.getRemainedKeyValuePairs(remainedKeys);
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Integrated Security',
                AzureSqlDatabaseLinkedService.integratedSecurity
            );
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Encrypt',
                AzureSqlDatabaseLinkedService.encrypt
            );
            builder.removeDefaultKeyValuePairs(
                linkedService.additionalFields,
                'Connection Timeout',
                AzureSqlDatabaseLinkedService.connectionTimeout
            );
        }
    }
}
